/**
 * Operator-facing copy for anything `fuelCardsService` rejects with.
 *
 * Shared by the page, the link picker and the history drawer so the two rulings
 * below are stated once. Both matter enough that a second copy would eventually
 * drift from the first.
 *
 * 1. **A 403 is always access control, never Comdata.** A missing endpoint
 *    grant, no group memberships, or the fuel-card routes not yet deployed in
 *    this environment (migration 433) — and until that migration lands, every
 *    route here 403s. Blaming Comdata would send whoever reads it to the wrong
 *    system. Pass `forbidden` to name the specific thing that was refused.
 * 2. **`?? message` is load-bearing.** `bulk()` rejects an empty or over-cap
 *    selection with a BARE `Error` that has no `response` at all, and it is the
 *    only rejection in the service shaped that way. A catch written only for the
 *    Axios shape renders nothing for it.
 * 3. **A 429 is Comdata's on exactly two routes: block and unblock.** Calling
 *    Corpay is not the test — surfacing its 429 is. Five routes call Corpay
 *    (block, unblock, bulk, sync, block-reasons) but three of them swallow the
 *    rate limit and answer 200: `bulk` puts "rate limited (429)" in the
 *    per-card results and sets `stopped_early`, `run_card_sync` folds it into a
 *    `warnings` entry, and `/block-reasons` falls back to its static list.
 *    `_run_action` is the only place in the endpoint module that raises
 *    `HTTPException(429)`. So a 429 arriving at the client from anywhere else —
 *    bulk and sync included — is gateway throttling, and naming Comdata there
 *    sends whoever reads it to the wrong vendor. That is the same category
 *    error the 403 ruling exists to prevent, pointed the other way. The default
 *    blames nobody; the two honest call sites pass `rateLimited`.
 */

/** What a rejection from this service can look like. Narrowed rather than typed
 *  `any`, because BOTH shapes are real: an AxiosError and a bare `Error`. */
type ThrownError = { response?: { status?: number; data?: { detail?: string } }; message?: string };

const asThrown = (e: unknown): ThrownError => (e ?? {}) as ThrownError;

export const FORBIDDEN_GENERIC = 'You do not have permission to do that. A 403 here is always access '
  + 'control — a missing endpoint grant, no group memberships, or the fuel-card routes not being '
  + 'deployed in this environment yet (migration 433).';

/** For the only two routes that surface Corpay's own 429: block and unblock. */
export const RATE_LIMITED_COMDATA = 'Comdata is rate limiting us. Try again in a minute.';

const RATE_LIMITED_GENERIC = 'Too many requests right now. Try again in a minute.';

export const errMessage = (
  e: unknown,
  fallback = 'The request failed.',
  forbidden = FORBIDDEN_GENERIC,
  rateLimited = RATE_LIMITED_GENERIC,
): string => {
  const err = asThrown(e);
  const status = err.response?.status;
  if (status === 403) return forbidden;
  if (status === 429) return rateLimited;
  return err.response?.data?.detail ?? err.message ?? fallback;
};
